import { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Search, Building2, Layers, ChevronDown, ChevronRight, Eye } from 'lucide-react'
import { supabase, isDemoMode } from '../lib/supabase'
import { DemoNotice } from '../lib/demo'

export default function Companies() {
  const [rows, setRows] = useState([])
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(false)
  const [q, setQ] = useState('')
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newGroupId, setNewGroupId] = useState('')
  const [collapsedGroups, setCollapsedGroups] = useState(new Set())

  const load = async () => {
    if (isDemoMode) return
    setLoading(true)
    const [companiesRes, groupsRes] = await Promise.all([
      supabase.from('companies')
        .select('id, name, contact_name, contact_email, archived_at, client_group_id')
        .is('archived_at', null).order('name'),
      supabase.from('client_groups').select('id, name').is('archived_at', null).order('name'),
    ])
    setRows(companiesRes.data || [])
    setGroups(groupsRes.data || [])
    setLoading(false)
  }
  useEffect(() => { load() }, [])

  const create = async (e) => {
    e.preventDefault()
    if (!newName.trim() || isDemoMode) return
    const { error } = await supabase.from('companies').insert({
      name: newName.trim(),
      client_group_id: newGroupId || null,
    })
    if (error) return alert(error.message)
    setNewName('')
    setNewGroupId('')
    setCreating(false)
    load()
  }

  const filtered = rows.filter(r => r.name.toLowerCase().includes(q.toLowerCase()))

  // Group by client_group; "ungrouped" sites collected separately.
  const grouped = useMemo(() => {
    const byGroup = new Map()
    const ungrouped = []
    for (const c of filtered) {
      if (c.client_group_id) {
        if (!byGroup.has(c.client_group_id)) byGroup.set(c.client_group_id, [])
        byGroup.get(c.client_group_id).push(c)
      } else {
        ungrouped.push(c)
      }
    }
    const sections = groups
      .filter(g => byGroup.has(g.id))
      .map(g => ({ id: g.id, name: g.name, sites: byGroup.get(g.id) }))
    if (ungrouped.length > 0) sections.push({ id: null, name: 'Ungrouped', sites: ungrouped })
    return sections
  }, [filtered, groups])

  const toggleGroup = (gid) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev)
      if (next.has(gid)) next.delete(gid); else next.add(gid)
      return next
    })
  }

  return (
    <div className="space-y-4">
      <DemoNotice />

      <div className="card">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text" value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search companies…" className="input-field pl-9"
            />
          </div>
          <Link to="/admin/client-groups" className="btn-secondary text-sm">
            <Layers className="w-4 h-4" /> Manage groups
          </Link>
          <button onClick={() => setCreating(c => !c)} className="btn-primary">
            <Plus className="w-4 h-4" /> New Company
          </button>
        </div>

        {creating && (
          <form onSubmit={create} className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_auto_auto] gap-2 mb-4 p-3 bg-gray-50 rounded-lg">
            <input
              type="text" value={newName} onChange={e => setNewName(e.target.value)}
              placeholder="Site name" className="input-field" autoFocus required
            />
            <select value={newGroupId} onChange={e => setNewGroupId(e.target.value)} className="input-field">
              <option value="">— no group —</option>
              {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
            <button type="submit" className="btn-primary">Create</button>
            <button type="button" onClick={() => { setCreating(false); setNewName(''); setNewGroupId('') }} className="btn-secondary">Cancel</button>
          </form>
        )}

        {loading && <p className="text-sm text-gray-500">Loading…</p>}

        {!loading && grouped.length === 0 && (
          <p className="text-sm text-gray-500 py-6 text-center">
            {rows.length === 0 ? 'No companies yet — add one above.' : 'No matches.'}
          </p>
        )}

        <div className="space-y-4">
          {grouped.map(section => {
            const collapsed = collapsedGroups.has(section.id)
            return (
              <div key={section.id ?? 'ungrouped'}>
                <button
                  type="button"
                  onClick={() => toggleGroup(section.id)}
                  className="w-full flex items-center gap-2 py-2 -mx-2 px-2 hover:bg-gray-50 rounded-lg"
                >
                  {collapsed ? <ChevronRight className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  <Layers className="w-4 h-4 text-brand" />
                  <span className="font-semibold text-gray-800">{section.name}</span>
                  <span className="text-xs text-gray-500">{section.sites.length} site{section.sites.length === 1 ? '' : 's'}</span>
                  {section.id && (
                    <Link to={`/admin/client-groups/${section.id}`} onClick={e => e.stopPropagation()} className="ml-auto text-xs text-brand-dark hover:underline">
                      Manage group
                    </Link>
                  )}
                </button>
                {!collapsed && (
                  <ul className="divide-y divide-gray-100 ml-6 mt-1">
                    {section.sites.map(c => (
                      <li key={c.id} className="flex items-center gap-1">
                        <Link to={`/companies/${c.id}`} className="flex-1 flex items-center gap-3 py-2 hover:bg-gray-50 -mx-2 px-2 rounded-lg transition-colors">
                          <Building2 className="w-5 h-5 text-brand" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-800 truncate">{c.name}</div>
                            {(c.contact_name || c.contact_email) && (
                              <div className="text-xs text-gray-500 truncate">
                                {[c.contact_name, c.contact_email].filter(Boolean).join(' · ')}
                              </div>
                            )}
                          </div>
                        </Link>
                        <a
                          href={`/?preview=${c.id}`} target="_blank" rel="noopener"
                          onClick={e => e.stopPropagation()}
                          className="text-xs text-brand-dark hover:bg-brand-50 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-brand/30 hover:border-brand transition-colors whitespace-nowrap"
                          title="Open the client portal in a new tab, as this site's users would see it"
                        >
                          <Eye className="w-3.5 h-3.5" /> Preview as client
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
